"use client";

import { useGetCurrentUser } from "@/hooks/auth-hook";
import { useUnreadCount } from "@/hooks/notification-hooks";
import { cartItem } from "@/redux/slices/cartSlice";
import { compareItem } from "@/redux/slices/compareSlice";
import { IconBtn } from "@/utils/main-utils";
import { useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { CiUser } from "react-icons/ci";
import { HiMiniShoppingCart } from "react-icons/hi2";
import { IoIosNotifications } from "react-icons/io";
import { LuRefreshCcw } from "react-icons/lu";
import { useSelector } from "react-redux";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationsList from "./Notification";

// ── TopBar ───────────────────────────────────────────────────────
export default function TopBar({
  isHome,
  show,
  setShow,
}: {
  isHome: boolean;
  show: boolean;
  setShow: any;
}) {
  const { data } = useGetCurrentUser();
  const queryClient = useQueryClient();
  const cart = useSelector(cartItem);
  const compare = useSelector(compareItem);
  const user = data?.user;

  const [cartItems] = useState<any>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [mounted, setMounted] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount } = useUnreadCount(!!user);

  const cartLength: number = user
    ? (cartItems?.items?.length ?? 0)
    : (cart?.length ?? 0);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!show) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [show, setShow]);

  // SSE
  useEffect(() => {
    const es = new EventSource(
      `${process.env.NEXT_PUBLIC_API_URI}/api/v1/notifications/stream`,
    );

    es.onmessage = (event) => {
      const d = JSON.parse(event.data);

      if (d?.property) {
        setNotifications((p) => [d.property, ...p]);
      }

      const invalidations: any = {
        REFRESH_PROPERTIES: ["properties", "property-details"],
        REFRESH_ORDERS: ["cart", "properties"],
        REFRESH_PROGRAMS: ["bank-programs", "program"],
        REFRESH_ADS: ["ads"],
        REFRESH_ACCOUNT: ["account"],
        REFRESH_PROJECTS: ["realEstateProjects"],
        REFRESH_UNITS: ["projectUnits", "unitDetails"],
      };

      const keys = invalidations[d.type];
      if (keys) {
        keys.forEach((key: string) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        );
      }

      // refresh persistent notifications when something new lands
      if (d.type === "REFRESH_PROPERTIES") {
        queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
        queryClient.invalidateQueries({ queryKey: ["notifications-inbox"] });
      }
    };

    es.onerror = () => es.close(); // ✅ handle errors

    return () => es.close();
  }, []);

  return (
    <div
      className={`
        relative z-50 py-5
        ${isHome ? "bg-transparent" : "bg-[#0f1117] border-b border-white/6"}
      `}
    >
      {!isHome && (
        <div className="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-emerald-500/30 to-transparent pointer-events-none" />
      )}

      <div className="sm:container px-4 md:px-0 lg:w-[85%] mx-auto">
        <div className="flex items-center gap-1.5 py-2">
          <LanguageSwitcher />

          <div className="flex items-center gap-1.5">
            {/* Cart */}
            {mounted && (
              <IconBtn
                href="/cart"
                badge={cartLength}
                badgeColor="teal"
                label="السلة"
              >
                <HiMiniShoppingCart size={17} />
              </IconBtn>
            )}

            {/* Compare */}
            {mounted && (
              <IconBtn
                href="/comparison"
                badge={compare?.length}
                badgeColor="teal"
                label="المقارنة"
              >
                <LuRefreshCcw size={16} />
              </IconBtn>
            )}

            {/* Notifications — only for logged-in users */}
            {mounted && user && (
              <div ref={notifRef} className="relative">
                <IconBtn
                  onClick={() => setShow((p: boolean) => !p)}
                  badge={unreadCount || 0}
                  badgeColor="red"
                  label="الإشعارات"
                >
                  <IoIosNotifications size={18} />
                </IconBtn>

                {show && (
                  <div className="absolute top-full mt-2 right-0 z-50">
                    <NotificationsList setShow={setShow} />
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="w-px h-4 bg-white/10 mx-1" />

            {/* User */}
            {mounted && user ? (
              <Link href="/profile" className="group relative shrink-0">
                <div
                  className="
                    w-8 h-8 rounded-2xl overflow-hidden relative
                    ring-1 ring-white/10
                    group-hover:ring-emerald-400/50
                    transition-all duration-150
                    group-hover:scale-105
                  "
                >
                  <Image
                    src={user?.photo || "/user.webp"}
                    alt={user?.username || "User"}
                    fill
                    className="object-cover"
                    priority
                  />
                </div>
                {/* نقطة أونلاين */}
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-[#0f1117]" />
              </Link>
            ) : (
              <IconBtn href="/auth/login" label="تسجيل الدخول">
                <CiUser size={18} />
              </IconBtn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
